/**
 * OFBiz 风格接口的 HTTP 基座
 * ------------------------------------------------------------------
 * 与旧的 OData(/bods.svc/)、REST(/rest/) 体系完全隔离，使用独立的
 * axios 实例，避免污染全局拦截器。
 *
 * - baseURL：同源部署 + nginx 反代，前缀固定为 /itoss/control/json/
 * - 鉴权：登录成功后由后端写入 JSESSIONID Cookie，浏览器自动携带
 *         （withCredentials=true）。无需手动拼 token。
 * - 响应：统一结构 { code, success, message, data }
 *         code===200 视为成功，直接返回 data；否则抛错。
 *         分页接口 data 形如 { list, total, page, pageSize }。
 *
 * 用法：
 *   import { ofbiz } from '../server/ofbiz/http';
 *   const tree = await ofbiz.post('monitor/tree', null, { groupId });
 *   const res  = await ofbiz.postPage('monitor/list', { monitorType: 'IT_MON_CPU' });
 *   //  res => { list, total, page, pageSize }
 */
import axios from 'axios';
import Qs from 'qs';
import { message, notification } from 'antd';
import Store from '../../script/store';

// 后端地址：打包时可由构建注入 __API_BASE__（如 https://10.112.204.121:8443），
// 未注入则回退 location.origin（同源部署 + nginx 反代到后端 8443）。
const OFBIZ_BASE = `${__API_BASE__ || location.origin}/itoss/control/json/`;

// 独立 axios 实例，不共享 src/common/http/index.js 的全局拦截器
const instance = axios.create({
  baseURL: OFBIZ_BASE,
  withCredentials: true, // 携带 JSESSIONID Cookie，跨域时后端需配 CORS
  timeout: 30000,
  headers: { 'Content-Type': 'application/json;charset=UTF-8' },
});

// 记录是否正在处理登出跳转，避免重复弹窗/跳转
let _redirecting = false;

/**
 * 业务层统一的请求方法。
 * @param {string} action   模块/动作，如 'monitor/tree'、'auth/login'
 * @param {object} body     JSON 请求体（无则传 null/undefined）
 * @param {object} params   query 参数（拼到 URL 上）
 * @param {object} options  额外配置：{ form:true } 表示用表单方式提交
 * @returns {Promise<any>}  成功 resolve(data)；失败 reject(Error)
 */
function request(action, body, params, options = {}) {
  const config = {
    url: action,
    method: 'post',
    params,
  };
  if (options.form) {
    // 登录等接口要求 application/x-www-form-urlencoded。
    // axios 对普通对象不会自动序列化，需手动转 querystring（项目已依赖 qs）。
    config.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    config.data = Qs.stringify(body || {});
  } else {
    config.data = body || {};
  }

  const silent = options.silent; // silent=true 时业务错误不自动弹通用提示，由调用方处理

  return instance.request(config).then(
    (resp) => {
      const payload = resp.data || {};
      // 统一响应结构 { code, success, message, data }
      if (payload.code === 200) {
        return payload.data;
      }
      // 401 在 success 分支里也可能出现（后端用 200 包了 401 语义）
      if (payload.code === 401) {
        handleUnauthorized(payload.message);
        return Promise.reject(toError(payload));
      }
      // 其它业务错误
      const err = toError(payload);
      if (!silent) {
        showError(err);
      }
      return Promise.reject(err);
    },
    (error) => {
      // HTTP 层错误（后端用 HTTP 状态码 400/401/403/500 而非 200 包业务码）
      const resp = error.response;
      const status = resp && resp.status;
      // 提取后端返回的 JSON body（{code, success, message, data}）作为错误信息
      const payload = (resp && resp.data) || {};
      const err = new Error(payload.message || error.message || '请求失败');
      err.code = payload.code || status;
      err.payload = payload;
      err.status = status;

      if (status === 401) {
        handleUnauthorized(payload.message);
      } else if (status === 403 && !silent) {
        notification.warning({ message: payload.message || '无操作权限' });
      } else if (status && status >= 500 && !silent) {
        notification.warning({
          message: `服务器异常 (${status})`,
          description: payload.message || '接口请求有误，请联系管理员',
        });
      } else if (status === 400 && !silent) {
        // 业务参数错误，显示后端返回的 message
        showError(err);
      }
      return Promise.reject(err);
    },
  );
}

function toError(payload) {
  const err = new Error(payload.message || '请求失败');
  err.code = payload.code;
  err.payload = payload;
  return err;
}

function showError(err) {
  // 业务错误统一用 message 轻提示
  message.warn(err.message || '操作失败');
}

function handleUnauthorized(msg) {
  if (_redirecting) return;
  _redirecting = true;
  notification.warning({
    message: '登录已过期',
    description: msg || '请重新登录',
  });
  // 清理本地登录态后跳登录页
  Store.deleted('token');
  setTimeout(() => {
    _redirecting = false;
    if (location.hash !== '#/login' && location.pathname !== '/login') {
      window.location.href = '/#/login';
    }
  }, 800);
}

/**
 * 对外暴露的 OFBiz 客户端。
 * - ofbiz.post(action, body, params)          => 普通 JSON 请求
 * - ofbiz.postForm(action, formBody, params)  => 表单请求（登录等）
 * - ofbiz.postPage(action, query, page, size) => 分页请求，返回 { list,total,page,pageSize }
 */
export const ofbiz = {
  /** 普通 JSON POST：返回 data。第四个参数 options.silent=true 时业务错误不弹通用提示 */
  post(action, body, params, options) {
    return request(action, body, params, options);
  },
  /** 表单 POST（Content-Type: x-www-form-urlencoded） */
  postForm(action, formBody, params) {
    return request(action, formBody, params, { form: true });
  },
  /**
   * 分页查询。query 为业务筛选条件，page/pageSize 默认 1/20。
   * @returns {Promise<{list:Array, total:number, page:number, pageSize:number}>}
   */
  postPage(action, query = {}, page = 1, pageSize = 20) {
    return request(action, null, { page, pageSize, ...query });
  },
  /** baseURL（调试用） */
  baseURL: OFBIZ_BASE,
};
